import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Search, ArrowLeft, Copy, Check, Loader2, History } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import VoteWidget from "../components/VoteWidget";
import ReviewWidget from "../components/ReviewWidget";
import { useLanguage } from "../contexts/LanguageContext";
import { useSearchHistory } from "../hooks/useSearchHistory";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const APP_DOMAIN = process.env.REACT_APP_DOMAIN || window.location.origin;
const DOWNLOADER_URL = process.env.REACT_APP_DOWNLOADER_URL || 'https://aftv.news';
const API = `${BACKEND_URL}/api`;

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { addToHistory, searchHistory } = useSearchHistory();
  const queryParam = searchParams.get("q") || searchParams.get("search") || "";
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [results, setResults] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);

  useEffect(() => {
    // Fetch results from API
    const fetchResults = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = searchQuery.trim() 
          ? `${API}/codes?q=${encodeURIComponent(searchQuery)}`
          : `${API}/codes`;
        
        const response = await axios.get(url);
        setResults(response.data.codes);
      } catch (err) {
        console.error("Error fetching codes:", err);
        setError("Failed to fetch codes. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchResults();
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newQuery = e.target.search.value.trim();
    if (newQuery) {
      addToHistory(newQuery);
      setSearchQuery(newQuery);
    }
  };
  
  const handleHistorySearch = (query) => {
    addToHistory(query);
    setSearchQuery(query);
  };

  const fetchSuggestions = async (query) => {
    // Always include history suggestions
    const historySuggestions = searchHistory.map(item => ({
      type: 'history',
      name: item.query,
      query: item.query,
      timestamp: item.timestamp
    }));

    if (query.length < 2) {
      setSuggestions(historySuggestions);
      setShowSuggestions(historySuggestions.length > 0);
      return;
    }

    try {
      const response = await axios.get(`${API}/search-suggestions?q=${encodeURIComponent(query)}`);
      const apiSuggestions = (response.data.suggestions || []).map(suggestion => ({
        type: 'api',
        name: suggestion,
        query: suggestion
      }));
      
      const filteredHistory = historySuggestions.filter(item =>
        item.query.toLowerCase().includes(query.toLowerCase())
      );
      
      const combinedSuggestions = [
        ...filteredHistory.slice(0, 3),
        ...apiSuggestions.slice(0, 7)
      ];
      
      setSuggestions(combinedSuggestions);
      setShowSuggestions(combinedSuggestions.length > 0);
      setSelectedSuggestion(-1);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions(historySuggestions);
      setShowSuggestions(historySuggestions.length > 0);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const query = suggestion.query || suggestion.name || suggestion;
    setSearchQuery(query);
    setShowSuggestions(false);
    addToHistory(query);
  };

  const handleInputFocus = () => {
    fetchSuggestions(searchQuery);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchSuggestions(value);
  };

  const copyToClipboard = async (code, name) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast({
        title: "Code copied!",
        description: `${name} code copied to clipboard`,
      });
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = code;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCode(code);
        toast({
          title: "Code copied!",
          description: `${name} code copied to clipboard`,
        });
        setTimeout(() => setCopiedCode(null), 2000);
      } catch (err2) {
        toast({
          title: "Copy failed",
          description: "Please copy the code manually",
          variant: "destructive",
        });
      }
      document.body.removeChild(textArea);
    }
  };

  const pageTitle = queryParam ? 
    `${queryParam} - Downloader Code Results | FireDL` : 
    'Search Downloader Codes | FireDL';
  
  const pageDescription = queryParam ? 
    `Find working Downloader codes for "${queryParam}". Copy verified codes for Firestick and Android TV instantly.` :
    'Search through thousands of working Downloader codes for Firestick and Android TV. Find verified codes for your favorite streaming apps.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${queryParam ? queryParam + ', ' : ''}Downloader codes, Firestick codes, Android TV codes, streaming app codes`} />
        <link rel="canonical" href={`${APP_DOMAIN}/searchresults${queryParam ? `?search=${encodeURIComponent(queryParam)}` : ''}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`${APP_DOMAIN}/searchresults${queryParam ? `?search=${encodeURIComponent(queryParam)}` : ''}`} />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {results.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SearchResultsPage",
              "mainEntity": {
                "@type": "ItemList",
                "numberOfItems": results.length,
                "itemListElement": results.slice(0, 5).map((result, index) => ({
                  "@type": "ListItem",
                  "position": index + 1,
                  "item": {
                    "@type": "SoftwareApplication",
                    "name": result.name,
                    "description": `Downloader code ${result.number} for ${result.name}`,
                    "applicationCategory": "EntertainmentApplication"
                  }
                }))
              }
            })}
          </script>
        )}
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4 mb-3 sm:mb-4">
            <button
              onClick={() => navigate("/")}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold logo-text-small">FIREDL</h1>
          </div>
          
          <form onSubmit={handleSearch} className="w-full">
            <div className="relative">
              <input
                type="text"
                name="search"
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={t('searchPlaceholder')}
              />
              
              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors ${
                        selectedSuggestion === index ? 'bg-blue-100' : ''
                      } ${suggestion.type === 'history' ? 'border-b border-gray-100' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {suggestion.type === 'history' ? (
                            <History className="w-4 h-4 text-purple-500 mr-3" />
                          ) : (
                            <Search className="w-4 h-4 text-gray-400 mr-3" />
                          )}
                          <span className="text-gray-900">{suggestion.name}</span>
                        </div>
                        {suggestion.type === 'history' && (
                          <span className="text-xs text-purple-500 font-medium">
                            {t('recent')}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 transition-colors"
                aria-label="Search"
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </form>
        </div>
      </header>

      {/* Results */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Search Results Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-500 mb-2">
            Search Results
          </h2>
          {!loading && (
            <p className="text-sm sm:text-base text-blue-400">
              {results.length} Active Downloader {results.length === 1 ? "Code" : "Codes"} Found
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-gray-500 mt-4">Loading codes...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 text-lg">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No codes found</p>
            <p className="text-gray-400 mt-2">Try a different search term</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              {/* Table Header */}
              <div className="bg-gray-100 border-b-2 border-gray-300 px-6 py-4">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3 text-left font-bold text-gray-700 text-base">
                    App Name
                  </div>
                  <div className="col-span-2 text-center font-bold text-gray-700 text-base">
                    Downloader Code
                  </div>
                  <div className="col-span-2 text-center font-bold text-gray-700 text-base">
                    Category
                  </div>
                  <div className="col-span-2 text-center font-bold text-gray-700 text-base">
                    Rating
                  </div>
                  <div className="col-span-3 text-center font-bold text-gray-700 text-base">
                    Last Check
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {results.map((item, index) => (
                  <div
                    key={item.id}
                    className={`px-6 py-5 hover:bg-blue-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* App Name */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-blue-500 font-medium text-lg hover:underline cursor-pointer">
                            {item.name}
                          </h3>
                        </div>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1">
                            {item.description}
                          </p>
                        )}
                      </div>

                      {/* Downloader Code */}
                      <div className="col-span-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <code className="text-blue-600 font-bold text-xl tracking-wide">
                            {item.code}
                          </code>
                          <button
                            onClick={() => copyToClipboard(item.code, item.name)}
                            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                            aria-label="Copy code"
                            title="Copy to clipboard"
                          >
                            {copiedCode === item.code ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          <a
                            href={`${DOWNLOADER_URL}/${encodeURIComponent(item.code)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-2 py-1 rounded transition-colors"
                            title="Download with Code"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.18L17.82 7 12 9.82 6.18 7 12 4.18zM6 8.82l5 2.5V18.82l-5-2.5V8.82zm12 0v7.5l-5 2.5v-7.5l5-2.5z"/>
                            </svg>
                            DL
                          </a>
                        </div>
                      </div>

                      {/* Category */}
                      <div className="col-span-2 text-center">
                        {item.category ? (
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {item.category}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </div>

                      {/* Rating */}
                      <div className="col-span-2 text-center">
                        <VoteWidget codeId={item.id} />
                        <div className="mt-2">
                          <ReviewWidget 
                            codeId={item.id} 
                            codeName={item.name}
                            codeNumber={item.code}
                          />
                        </div>
                      </div>

                      {/* Last Check */}
                      <div className="col-span-3 text-center">
                        {item.created_at ? (
                          <div className="text-sm">
                            <div className="text-gray-900 font-medium">
                              {new Date(item.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {new Date(item.created_at).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {results.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="space-y-3">
                    {/* App Name */}
                    <div>
                      <h3 className="text-blue-500 font-semibold text-lg mb-1">
                        {item.name}
                      </h3>
                      {item.description && (
                        <p className="text-xs text-gray-500">
                          {item.description}
                        </p>
                      )}
                    </div>

                    {/* Category and Last Check */}
                    <div className="flex items-center justify-between">
                      <div>
                        {item.category && (
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {item.category}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Last Check</div>
                        {item.created_at ? (
                          <div className="text-xs">
                            <div className="text-gray-900 font-medium">
                              {new Date(item.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-gray-500">
                              {new Date(item.created_at).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </div>
                    </div>

                    {/* Code and Actions */}
                    <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <code className="text-blue-600 font-bold text-xl">
                          {item.code}
                        </code>
                        <button
                          onClick={() => copyToClipboard(item.code, item.name)}
                          className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                          aria-label="Copy code"
                        >
                          {copiedCode === item.code ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <Copy className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                      </div>

                      {/* Download Button */}
                      <a
                        href={`${DOWNLOADER_URL}/${encodeURIComponent(item.code)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors w-full"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.18L17.82 7 12 9.82 6.18 7 12 4.18zM6 8.82l5 2.5V18.82l-5-2.5V8.82zm12 0v7.5l-5 2.5v-7.5l5-2.5z"/>
                        </svg>
                        Get Downloader App
                      </a>

                      {/* Rating */}
                      <div className="pt-2">
                        <div className="text-xs text-gray-600 mb-2 font-medium">Rate this code:</div>
                        <VoteWidget codeId={item.id} />
                      </div>

                      {/* Review */}
                      <div className="pt-3 border-t border-gray-200">
                        <ReviewWidget 
                          codeId={item.id} 
                          codeName={item.name}
                          codeNumber={item.code}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <Toaster />
    </div>
    </>
  );
};

export default SearchResults;
